"use client";

import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon, SearchIcon } from "lucide-react";

import { cn } from "./utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./dialog";

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
    return (
        <CommandPrimitive
            data-slot="command"
            className={cn(
                "flex flex-col bg-popover rounded-xl! size-full overflow-hidden text-popover-foreground",
                className
            )}
            {...props}
        />
    );
}

function CommandDialog({
    title = "Command Palette",
    description = "Search for a command to run...",
    children,
    className,
    ...props
}: React.ComponentProps<typeof Dialog> & {
    title?: string;
    description?: string;
    className?: string;
}) {
    return (
        <Dialog {...props}>
            <DialogHeader className="sr-only">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogContent className={cn("top-1/3 p-0 rounded-xl! overflow-hidden translate-y-0", className)}>
                {children}
            </DialogContent>
        </Dialog>
    );
}

function CommandInput({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Input>) {
    return (
        <div data-slot="command-input-wrapper" className="border-border border-b">
            <div className="flex items-center gap-2 px-2 py-1">
                <SearchIcon className="opacity-50 size-4 shrink-0" />
                <CommandPrimitive.Input
                    data-slot="command-input"
                    className={cn(
                        "flex-1 bg-transparent disabled:opacity-50 shadow-none px-0 py-1 border-0 outline-hidden focus:outline-none ring-0 focus:ring-0 text-sm disabled:cursor-not-allowed",
                        className
                    )}
                    {...props}
                />
            </div>
        </div>
    );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
    return (
        <CommandPrimitive.List
            data-slot="command-list"
            className={cn(
                "outline-none max-h-72 overflow-x-hidden overflow-y-auto scroll-py-1 no-scrollbar",
                className
            )}
            {...props}
        />
    );
}

function CommandEmpty({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
    return (
        <CommandPrimitive.Empty
            data-slot="command-empty"
            className={cn("py-6 text-sm text-center", className)}
            {...props}
        />
    );
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
    return (
        <CommandPrimitive.Group
            data-slot="command-group"
            className={cn(
                "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-1 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-slate-600 dark:[&_[cmdk-group-heading]]:text-slate-300",
                className
            )}
            {...props}
        />
    );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
    return (
        <CommandPrimitive.Separator
            data-slot="command-separator"
            className={cn("-mx-1 bg-border h-px", className)}
            {...props}
        />
    );
}

function CommandItem({
    className,
    children,
    showCheck = true,
    ...props
}: React.ComponentProps<typeof CommandPrimitive.Item> & { showCheck?: boolean }) {
    return (
        <CommandPrimitive.Item
            data-slot="command-item"
            className={cn(
                "group/command-item relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none in-data-[slot=dialog-content]:rounded-lg! data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-selected:bg-indigo-100 data-selected:text-slate-900 dark:data-selected:bg-indigo-800 dark:data-selected:text-slate-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-selected:*:[svg]:text-foreground",
                className
            )}
            {...props}
        >
            {children}
            {showCheck ? (
                <CheckIcon className="group-has-data-[slot=command-shortcut]/command-item:hidden opacity-0 group-data-[checked=true]/command-item:opacity-100 ml-auto" />
            ) : null}
        </CommandPrimitive.Item>
    );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="command-shortcut"
            className={cn(
                "ml-auto text-muted-foreground group-data-selected/command-item:text-foreground text-xs tracking-widest",
                className
            )}
            {...props}
        />
    );
}

export {
    Command,
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandShortcut,
    CommandSeparator,
};
